import {Component, computed, effect, inject, signal, untracked} from '@angular/core';
import {Router} from '@angular/router';
import {FormsModule} from '@angular/forms';
import {Button} from 'primeng/button';
import {Card} from 'primeng/card';
import {InputText} from 'primeng/inputtext';
import {ConfirmationService} from 'primeng/api';
import {DialogService} from 'primeng/dynamicdialog';
import {CreateTemplateBaseComponent} from '../create-template-base-component.directive';
import {CreateTemplateTravellers} from '../create-template-travellers/create-template-travellers';
import {CreateTemplateDetails} from '../create-template-details/create-template-details';
import {CreateTemplateItinerary} from '../create-template-itinerary/create-template-itinerary';
import {CreateTemplateStepModal} from '../create-template-step-modal/create-template-step-modal';
import {TEMPLATE_STEPS, TemplateStepCode} from '../../../../datasources/template.datasource';
import {CreateTaStepType} from '../../../../util/app.types';

/** Last template step: title, read-only summary of steps 1-3, Save and Delete. */
@Component({
  selector: 'create-template-review',
  imports: [
    FormsModule,
    Button,
    Card,
    InputText,
    CreateTemplateTravellers,
    CreateTemplateDetails,
    CreateTemplateItinerary,
  ],
  providers: [
    DialogService
  ],
  templateUrl: './create-template-review.html',
})
export class CreateTemplateReview extends CreateTemplateBaseComponent {
  private router = inject(Router);
  private confirmationService = inject(ConfirmationService);
  private dialogService = inject(DialogService);

  title = signal<string>('');
  titleValid = computed<boolean>(() => !!this.title().trim());
  private syncedTemplateId: string | null = null;

  protected readonly CreateTaStepType = CreateTaStepType;

  constructor() {
    super();
    // Seed the title once per template so a save response does not overwrite typing.
    effect(() => {
      const template = this.template();
      if (template?.id && template.id !== this.syncedTemplateId) {
        this.syncedTemplateId = template.id;
        untracked(() => this.title.set(template.name ?? ''));
      }
    });
  }

  openEditModal(step: TemplateStepCode): void {
    const title = TEMPLATE_STEPS.find(s => s.code === step)?.title;
    this.dialogService.open(CreateTemplateStepModal, {
      header: 'Editing Template ' + (this.template()?.name ?? '') + ' - ' + title,
      inputValues: {
        currentStep: step,
      },
      contentStyle: {
        overflow: 'auto'
      },
      modal: true,
      width: '90%',
      focusOnShow: false,
    });
  }

  async saveTemplate(): Promise<void> {
    const name = this.title().trim();
    if (!name) return;
    if (name !== this.template()?.name) {
      const saved = await this.templateDatasource.saveField('name', name, {name});
      if (!saved) return;
    }
    this.router.navigate(['/home'], {queryParams: {tab: 'templates'}});
  }

  deleteTemplate(): void {
    const id = this.template()!.id!;
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this template? This cannot be undone.',
      header: 'Confirm Delete Template',
      icon: 'pi pi-info-circle',
      rejectButtonProps: {
        label: 'Close',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Delete Template',
        severity: 'danger',
      },
      accept: async () => {
        if (await this.templateDatasource.deleteTemplate(id)) {
          this.router.navigate(['/home'], {queryParams: {tab: 'templates'}});
        }
      },
    });
  }
}
